import type { MyInviteDto } from '@dijitalapp/shared';
import { lazy, Suspense, useEffect, useState } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';
import { UserAvatar } from './UserAvatar';

// The QR code drawing is only needed when asked for.
const QrCode = lazy(() => import('./QrLogin').then((m) => ({ default: m.QrCode })));

/**
 * "Invite a friend": one personal link to share in any way (system share sheet, WhatsApp, email, QR, copy).
 * Whoever signs up with it becomes a contact (adults), and appears in the list below.
 */
export function InviteFriends() {
  const { i18n, config } = useApp();
  const { t, formatDate } = i18n;
  const [mine, setMine] = useState<MyInviteDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showJoined, setShowJoined] = useState(false);

  useEffect(() => {
    api<MyInviteDto>('GET', '/invites/mine').then(setMine, (err: unknown) => setError(t(errorMessageKey(err))));
  }, [t]);

  const create = async () => {
    setError(null);
    try {
      setMine(await api<MyInviteDto>('POST', '/invites/mine', {}));
      setCopied(false);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  if (!mine) return error ? <p className="alert alert-error">{error}</p> : null;
  const brand = config?.brand.name ?? 'DijitalApp';
  const message = mine.link ? t('invite.message', { brand, link: mine.link }) : '';
  const copy = async () => {
    if (!mine.link) return;
    try {
      await navigator.clipboard.writeText(mine.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const share = async () => {
    try {
      await navigator.share({ title: brand, text: t('invite.shareText', { brand }), url: mine.link! });
    } catch {
      /* closed or not supported: the other buttons remain */
    }
  };

  return (
    <section className="card stack invite-card" aria-labelledby="invite-title">
      <div className="invite-head">
        <span className="invite-mark" aria-hidden>
          <Icon name="userPlus" size={22} />
        </span>
        <div>
          <h2 id="invite-title" className="card-title">
            {t('invite.title', { brand })}
          </h2>
          <p className="muted">{t('invite.desc')}</p>
        </div>
      </div>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
      {!mine.allowed ? (
        <p className="hint">{t('invite.notAllowed')}</p>
      ) : !mine.link ? (
        <button className="btn btn-primary" type="button" onClick={() => void create()}>
          <Icon name="link" size={18} /> {t('invite.create')}
        </button>
      ) : (
        <>
          <div className="input-action">
            <input className="input" readOnly value={mine.link} aria-label={t('invite.link')} onFocus={(e) => e.target.select()} />
            <button className="btn btn-primary" type="button" onClick={() => void copy()}>
              <Icon name={copied ? 'check' : 'copy'} size={18} /> {copied ? t('invite.copied') : t('invite.copy')}
            </button>
          </div>
          <div className="invite-actions">
            {'share' in navigator && (
              <button className="btn" type="button" onClick={() => void share()}>
                <Icon name="send" size={18} /> {t('invite.share')}
              </button>
            )}
            <a className="btn" href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer">
              <Icon name="messages" size={18} /> WhatsApp
            </a>
            <a className="btn" href={`mailto:?subject=${encodeURIComponent(t('invite.mailSubject', { brand }))}&body=${encodeURIComponent(message)}`}>
              <Icon name="send" size={18} /> {t('invite.email')}
            </a>
            <button className="btn" type="button" aria-expanded={showQr} onClick={() => setShowQr((x) => !x)}>
              <Icon name="monitor" size={18} /> {t('invite.qr')}
            </button>
          </div>
          {showQr && (
            <div className="invite-qr">
              <Suspense fallback={<p className="muted">{t('app.loading')}</p>}>
                <QrCode text={mine.link} size={200} label={t('invite.qrLabel')} />
              </Suspense>
              <p className="hint">{t('invite.qrHint')}</p>
            </div>
          )}
          <div className="row invite-foot">
            <span className="muted">{t('invite.used', { count: mine.usedCount, max: mine.maxUses })}</span>
            <span className="spacer" />
            <button className="link-button" type="button" onClick={() => window.confirm(t('invite.renewConfirm')) && void create()}>
              {t('invite.renew')}
            </button>
          </div>
        </>
      )}
      {mine.joined.length > 0 && (
        <div className="stack invite-joined">
          <button className="link-button" type="button" aria-expanded={showJoined} onClick={() => setShowJoined((x) => !x)}>
            {t('invite.joinedCount', { count: mine.joined.length })}
          </button>
          {showJoined && (
            <ul className="plain-list">
              {mine.joined.map((j) => (
                <li key={j.id} className="toggle-row">
                  <UserAvatar userId={j.id} name={j.displayName} />
                  <div style={{ minWidth: 0 }}>
                    <strong>{j.displayName}</strong> <span className="muted">@{j.username}</span>
                    <p className="hint">{t('invite.joinedOn', { date: formatDate(j.joinedAt, { dateStyle: 'medium' }) })}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
